import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { StoredPacket, StoredNode } from '../storage/db';
import { FusionEngine } from '../storage/fusionEngine';
import { GeoSnapshot, calculateGeoDistanceKm } from '../geo/locationTracker';
import { getNodeColor, getContrastTextColor } from './nodeColor';
import { SpatialCoverageManager, CoverageSpotData } from './spatialCoverage';
import { HexGridManager, HexCellData } from './hexGrid';

export interface MarkerColorConfig {
  color: string;
  fillColor: string;
  label: string;
}

/**
 * Returns color codes based on LoRa SNR (Signal-to-Noise Ratio) in dB.
 *
 * @param snr - Signal to noise ratio in dB
 * @returns Color hex codes and tactical label
 */
export function getSnrColor(snr: number): MarkerColorConfig {
  if (snr > 5) {
    // Excellent signal: Neon Cyan
    return { color: '#00f3ff', fillColor: '#00f3ff', label: 'Excellent (>5 dB)' };
  } else if (snr >= 0) {
    // Good signal: Neon Lime
    return { color: '#39ff14', fillColor: '#39ff14', label: 'Good (0..5 dB)' };
  } else if (snr >= -10) {
    // Marginal signal: Amber Yellow
    return { color: '#ffb700', fillColor: '#ffb700', label: 'Marginal (-10..0 dB)' };
  } else {
    // Critical / Edge of reception: Neon Red
    return { color: '#ff003c', fillColor: '#ff003c', label: 'Critical (<-10 dB)' };
  }
}

/**
 * Normalizes SNR into 0..1 range for heat map gradient weighting.
 *
 * @param snr - LoRa SNR value
 */
export function normalizeSnrForHeat(snr: number): number {
  // Typical Meshtastic LoRa SNR ranges between -20 dB and +12 dB
  const clamped = Math.max(-20, Math.min(12, snr));
  return (clamped + 20) / 32;
}

/**
 * Calculates great-circle distance between two GPS coordinates in kilometers.
 *
 * @param lat1 - Latitude of point 1
 * @param lon1 - Longitude of point 1
 * @param lat2 - Latitude of point 2
 * @param lon2 - Longitude of point 2
 * @returns Distance in kilometers formatted to 2 decimals
 */
export function getDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((R * c).toFixed(2));
}

/**
 * Manages the Leaflet interactive map, tactical dark layers, SNR marker visualization,
 * fixed radio node markers, dynamic routing hop vectors, and GPS breadcrumb trail.
 */
export class MapManager {
  private map: L.Map | null = null;
  private markerLayer = L.layerGroup();
  private hexLayer = L.layerGroup();
  private nodeLayer = L.layerGroup();
  private vectorLayer = L.layerGroup();
  private trackPolyline: L.Polyline | null = null;
  private heatLayer: L.HeatLayer | null = null;
  private userLocationMarker: L.CircleMarker | null = null;

  private activePackets: StoredPacket[] = [];
  private heatPoints: [number, number, number][] = [];
  private trackCoords: [number, number][] = [];
  private nodeMarkers: Map<number, L.Marker> = new Map();
  private nodeCoordsMap: Map<string, number[]> = new Map();
  private nodePrevCoord: Map<number, string> = new Map();
  private spotMarkers: Map<string, L.CircleMarker> = new Map();
  private spatialManager = new SpatialCoverageManager();
  private hexManager = new HexGridManager(26);
  private displayMode: 'hexagrid' | 'raw' = 'hexagrid';
  private hexRenderScheduled = false;
  private showNodeMarkers = true;
  private currentBasemap: 'osm-dark' | 'osm-standard' | 'satellite' = 'osm-dark';
  private baseLayers: Record<string, L.TileLayer> = {};
  private isFollowingLocation = false;
  private lastKnownLocation: GeoSnapshot | null = null;
  private followLocationListeners: ((active: boolean) => void)[] = [];
  private hexContributingNodes: Set<number> = new Set();
  private canvasRenderer: L.Canvas | undefined =
    typeof L.canvas === 'function' ? L.canvas({ padding: 0.5 }) : undefined;

  constructor(private fusionEngine: FusionEngine) {}

  /**
   * Initializes Leaflet map container and attaches base tile providers.
   *
   * @param elementId - HTML ID of the map div
   */
  init(elementId: string): void {
    // Default center on Europe or neutral coordinate until GPS fix arrives
    this.map = L.map(elementId, {
      center: [51.1657, 10.4515],
      zoom: 6,
      zoomControl: false
    });

    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // 100% Free & Keyless Tile Providers
    const osmDark = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      className: 'leaflet-tile-dark'
    });

    const osmStandard = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    });

    const esriSatellite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles &copy; Esri &mdash; Earthstar Geographics',
        maxZoom: 18
      }
    );

    this.baseLayers = {
      'osm-dark': osmDark,
      'osm-standard': osmStandard,
      'satellite': esriSatellite
    };

    // Set OpenStreetMap (Dark Mode) as default basemap
    osmDark.addTo(this.map);

    // Attach active layers (Hexagrid is active by default)
    this.nodeLayer.addTo(this.map);
    if (this.displayMode === 'hexagrid') {
      this.hexLayer.addTo(this.map);
    } else {
      this.markerLayer.addTo(this.map);
    }
    this.vectorLayer.addTo(this.map);

    // Dynamic zoom re-binning: updates hexagon geometry and groupings on zoom changes
    this.map.on('zoomend', () => {
      if (this.displayMode === 'hexagrid') {
        this.renderHexGrid();
      }
    });

    // Initialize track polyline
    this.trackPolyline = L.polyline([], {
      color: '#00f3ff',
      weight: 3,
      opacity: 0.6,
      dashArray: '4, 8',
      renderer: this.canvasRenderer
    }).addTo(this.map);

    // Initialize Heatmap layer (default: disabled, optional overlay)
    this.heatLayer = (L as unknown as { heatLayer: (latlngs: Array<L.LatLng | L.HeatLatLngTuple>, options: L.HeatMapOptions) => L.HeatLayer }).heatLayer([], {
      radius: 28,
      blur: 18,
      maxZoom: 17,
      gradient: {
        0.1: '#ff003c',
        0.4: '#ffb700',
        0.7: '#39ff14',
        1.0: '#00f3ff'
      }
    });

    // Automatically release GPS camera tracking when operator manually pans/drags the map with touch or mouse
    this.map.on('dragstart', () => {
      if (this.isFollowingLocation) {
        this.setFollowLocation(false);
      }
    });

    // Ensure Leaflet recalculates pixel dimensions when mobile address bars hide/show or device rotates
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.map?.invalidateSize();
      });
    }
  }

  /**
   * Switches tracking representation mode between aggregated Hexagrid and raw reception circles.
   *
   * Why: Hexagrid clusters signal reception into discrete geometric bins to prevent visual
   * clutter and reveal spatial coverage trends across zoom levels, while raw points allow
   * inspecting every individual discrete spot marker.
   *
   * @param mode - 'hexagrid' or 'raw'
   */
  setDisplayMode(mode: 'hexagrid' | 'raw'): void {
    if (this.displayMode === mode) return;
    this.displayMode = mode;

    if (!this.map) return;

    if (mode === 'hexagrid') {
      if (this.map.hasLayer(this.markerLayer)) {
        this.map.removeLayer(this.markerLayer);
      }
      if (!this.map.hasLayer(this.hexLayer)) {
        this.hexLayer.addTo(this.map);
      }
      this.renderHexGrid();
    } else {
      if (this.map.hasLayer(this.hexLayer)) {
        this.map.removeLayer(this.hexLayer);
      }
      if (!this.map.hasLayer(this.markerLayer)) {
        this.markerLayer.addTo(this.map);
      }
    }
  }

  /**
   * Retrieves the currently active tracking representation mode.
   */
  getDisplayMode(): 'hexagrid' | 'raw' {
    return this.displayMode;
  }

  /**
   * Debounces hexagon grid rendering via requestAnimationFrame to avoid recalculating
   * bin projections multiple times during rapid packet bursts.
   */
  private scheduleHexRender(): void {
    if (this.hexRenderScheduled) return;
    this.hexRenderScheduled = true;
    const raf = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame : (cb: () => void) => setTimeout(cb, 0);
    raf(() => {
      this.hexRenderScheduled = false;
      this.renderHexGrid();
    });
  }

  /**
   * Groups active packets into regular hexagonal screen-space bins at the current map zoom
   * and renders tactical polygons colored by dominant node with SNR-based opacity.
   *
   * Why: Regular hexagonal tessellation provides equal distance to all neighboring cells,
   * accurately conveying coverage density and boundaries without stationary spot pileup.
   */
  renderHexGrid(): void {
    this.hexLayer.clearLayers();
    if (!this.map) return;

    const zoom = this.map.getZoom();
    const cells = this.hexManager.binPackets(
      this.activePackets,
      zoom,
      (p) => this.resolveTransmittingNode(p)
    );

    this.hexContributingNodes.clear();
    for (const cell of cells) {
      if (cell.dominantNodeId !== undefined) {
        this.hexContributingNodes.add(Number(cell.dominantNodeId) >>> 0);
      }
    }
    this.updateNodeMarkerOpacities();

    for (const cell of cells) {
      const polygon = L.polygon(cell.polygon, {
        renderer: this.canvasRenderer,
        stroke: false,
        weight: 0,
        fillColor: cell.color,
        fillOpacity: cell.opacity
      });

      polygon.bindPopup(this.buildHexPopupContent(cell), { className: 'tactical-popup', closeButton: false });

      // Clear any active inspection vector when selecting a hexagon sector
      polygon.on('click', () => {
        this.clearInspectionVector();
      });

      this.hexLayer.addLayer(polygon);
    }
  }

  /**
   * Builds tactical popup HTML for an aggregated hexagon grid cell, detailing packet volume,
   * SNR metrics, and a multi-node transmitter breakdown if overlapping nodes were received.
   *
   * @param cell - Aggregated hexagon cell data
   * @returns Formatted HTML string
   */
  private buildHexPopupContent(cell: HexCellData): string {
    const multiNodeBadge =
      cell.isMultiNode
        ? `<span style="background: rgba(255,255,255,0.15); color: #fff; padding: 1px 5px; border-radius: 3px; font-size: 10px; margin-left: 6px; border: 1px dashed #fff;">${cell.nodesSummary.length} NODES</span>`
        : '';

    let nodeBreakdownHtml = '';
    if (cell.isMultiNode) {
      const nodeItems = cell.nodesSummary
        .map((n) => {
          const textColor = getContrastTextColor(n.color);
          return `
          <div style="display: flex; align-items: center; justify-content: space-between; font-size: 11px; padding: 2px 0; border-bottom: 1px dotted #334155;">
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="background: ${n.color}; color: ${textColor}; padding: 0 4px; border-radius: 2px; font-weight: bold; font-size: 10px;">${n.nodeName}</span>
            </div>
            <div style="font-family: monospace; color: #94a3b8;">
              <span style="color: ${n.color}; font-weight: bold;">${n.maxSnr} dB</span> / <span style="color: #fff;">${n.count}</span> pkts
            </div>
          </div>
        `;
        })
        .join('');

      nodeBreakdownHtml = `
        <div style="margin-top: 6px; border-top: 1px solid #334155; padding-top: 4px;">
          <div style="font-size: 11px; font-weight: bold; color: #94a3b8; margin-bottom: 2px;">Empfangene Nodes (nach SNR sortiert):</div>
          ${nodeItems}
        </div>
      `;
    }

    return `
      <div style="font-family: monospace; font-size: 12px; color: #fff; background: #0f172a; padding: 6px; border-radius: 4px; border-left: 4px solid ${cell.color}; min-width: 210px;">
        <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #334155; padding-bottom: 4px;">
          <span style="color: ${cell.color}; font-weight: bold;">⬡ Hex-Sektor: ${cell.dominantNodeName}</span>
          ${multiNodeBadge}
        </div>
        <div style="margin-top: 5px; line-height: 1.5;">
          <b>Stärkste Node:</b> <span style="color: ${cell.color}; font-weight: bold;">${cell.dominantNodeName}</span><br/>
          <b>Pakete im Sektor:</b> <span style="color: #fff; font-weight: bold;">${cell.packetCount}</span><br/>
          <b>Bester SNR:</b> <span style="color: ${cell.color}; font-weight: bold;">${cell.maxSnr} dB</span> (${Math.round(cell.opacity * 100)}% Deckkraft)<br/>
          <b>Durchschnitts-SNR:</b> ${cell.avgSnr.toFixed(1)} dB<br/>
          <b>Zentrum (GPS):</b> ${cell.center[0].toFixed(5)}, ${cell.center[1].toFixed(5)}<br/>
        </div>
        ${nodeBreakdownHtml}
        <div style="margin-top: 6px; font-size: 10px; color: #00f3ff; border-top: 1px solid #334155; padding-top: 3px;">
          📡 Klick aktiviert Verbindungslinie zur Node
        </div>
      </div>
    `;
  }

  /**
   * Toggles or sets the visibility of the signal heatmap layer.
   *
   * @param visible - Desired visibility state
   */
  setHeatmapVisible(visible: boolean): void {
    if (!this.map || !this.heatLayer) return;
    if (visible) {
      if (!this.map.hasLayer(this.heatLayer)) {
        this.heatLayer.addTo(this.map);
      }
    } else {
      if (this.map.hasLayer(this.heatLayer)) {
        this.map.removeLayer(this.heatLayer);
      }
    }
  }

  /**
   * Checks whether the heatmap layer is currently active on the map.
   */
  isHeatmapVisible(): boolean {
    return Boolean(this.map && this.heatLayer && this.map.hasLayer(this.heatLayer));
  }

  /**
   * Toggles visibility of the GPS track polyline on the map.
   *
   * Why: Enables operators to toggle the traveled breadcrumb route on and off cleanly.
   *
   * @param visible - Desired visibility state
   */
  setTrackVisible(visible: boolean): void {
    if (!this.map || !this.trackPolyline) return;
    if (visible) {
      if (!this.map.hasLayer(this.trackPolyline)) {
        this.trackPolyline.addTo(this.map);
      }
    } else {
      if (this.map.hasLayer(this.trackPolyline)) {
        this.map.removeLayer(this.trackPolyline);
      }
    }
  }

  /**
   * Checks whether the GPS track polyline is currently active on the map.
   */
  isTrackVisible(): boolean {
    return Boolean(this.map && this.trackPolyline && this.map.hasLayer(this.trackPolyline));
  }

  /**
   * Toggles visibility of fixed NodeDB radio station markers on the map.
   *
   * Why: NodeRadar primarily focuses on live over-the-air packet reception spots.
   * NodeDB entries downloaded from the radio can clutter the tactical view and are therefore
   * disabled by default, available as an optional overlay.
   *
   * @param visible - Boolean visibility flag
   */
  setNodeMarkersVisible(visible: boolean): void {
    if (!this.map) return;
    this.showNodeMarkers = visible;

    if (visible) {
      if (!this.map.hasLayer(this.nodeLayer)) {
        this.nodeLayer.addTo(this.map);
      }
    } else {
      if (this.map.hasLayer(this.nodeLayer)) {
        this.map.removeLayer(this.nodeLayer);
      }
    }
  }

  /**
   * Checks whether fixed NodeDB station markers are currently enabled on the map.
   */
  isNodeMarkersVisible(): boolean {
    return this.showNodeMarkers;
  }

  /**
   * Adds or updates a fixed radio node marker at its known geographic position.
   * Resolves co-location collisions by vertically offsetting nameplate badges sharing identical coordinates.
   *
   * @param node - Node record with latitude and longitude
   */
  updateNodeMarker(node: StoredNode): void {
    if (
      !this.map ||
      typeof node.lat !== 'number' ||
      typeof node.lon !== 'number' ||
      (node.lat === 0 && node.lon === 0)
    ) {
      return;
    }

    const nodeNum = Number(node.num) >>> 0;
    const lat = node.lat;
    const lon = node.lon;
    const latLng: [number, number] = [lat, lon];
    const nodeColor = getNodeColor(nodeNum, node.shortName);
    const textColor = getContrastTextColor(nodeColor);

    // Spatial key for co-location collision resolution (~1m accuracy: 5 decimals)
    const coordKey = `${lat.toFixed(5)},${lon.toFixed(5)}`;
    const prevCoord = this.nodePrevCoord.get(nodeNum);

    if (prevCoord && prevCoord !== coordKey) {
      const prevList = this.nodeCoordsMap.get(prevCoord);
      if (prevList) {
        const idx = prevList.indexOf(nodeNum);
        if (idx !== -1) prevList.splice(idx, 1);
      }
    }
    this.nodePrevCoord.set(nodeNum, coordKey);

    let coLocated = this.nodeCoordsMap.get(coordKey);
    if (!coLocated) {
      coLocated = [];
      this.nodeCoordsMap.set(coordKey, coLocated);
    }
    if (!coLocated.includes(nodeNum)) {
      coLocated.push(nodeNum);
    }
    const stackIndex = coLocated.indexOf(nodeNum);
    const offsetY = stackIndex * 22; // 22px vertical offset per co-located badge

    const isContributing = this.hexContributingNodes.has(nodeNum);
    const dimClass = isContributing ? '' : ' dimmed';

    const icon = L.divIcon({
      className: 'custom-node-marker-wrapper',
      html: `<div class="node-nameplate${dimClass}" style="background-color: ${nodeColor}; color: ${textColor}; transform: translate(-50%, calc(-50% + ${offsetY}px));">${node.shortName}</div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0]
    });

    const displayName = (node.longName || node.shortName || node.hexId).replace(/"/g, '&quot;');
    const popupContent = `
      <div style="font-family: monospace; font-size: 12px; color: #fff; background: #0f172a; padding: 6px; border-radius: 6px; min-width: 200px; border-left: 4px solid ${nodeColor};">
        <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #334155; padding-bottom: 4px;">
          <span style="font-weight: bold; color: #fff;">${node.longName}</span>
          <span style="background: ${nodeColor}; color: ${textColor}; padding: 1px 6px; border-radius: 3px; font-weight: bold; font-size: 11px;">${node.shortName}</span>
        </div>
        <div style="margin-top: 5px; line-height: 1.5;">
          <b>Node ID:</b> <span style="color: #38bdf8;">${node.hexId}</span><br/>
          <b>Position:</b> ${lat.toFixed(5)}, ${lon.toFixed(5)}<br/>
          ${node.lastSnr !== undefined ? `<b>Letzter SNR:</b> ${node.lastSnr} dB<br/>` : ''}
          <b>Zuletzt gehört:</b> ${new Date(node.lastHeard * 1000).toLocaleTimeString()}<br/>
        </div>
        <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #334155; display: flex; flex-direction: column; gap: 6px;">
          <button class="btn btn-sm btn-cyan btn-isolate-node" data-node-num="${nodeNum}" data-node-name="${displayName}" style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 5px; font-size: 11px; padding: 5px 8px; cursor: pointer;">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/></svg>
            <span>Node isolieren</span>
          </button>
          <div style="display: flex; gap: 6px; width: 100%;">
            <button class="btn btn-sm btn-secondary btn-auto-traceroute-node" data-node-num="${nodeNum}" data-node-name="${displayName}" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 4px; font-size: 11px; padding: 5px 6px; cursor: pointer;" title="Automatische periodische Traceroute für diesen Node starten">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
              <span>Auto Traceroute</span>
            </button>
            <button class="btn btn-sm btn-secondary btn-traceroute-once-node" data-node-num="${nodeNum}" data-node-name="${displayName}" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 4px; font-size: 11px; padding: 5px 6px; cursor: pointer;" title="Einmaligen Traceroute-Ping an diesen Node senden">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8a6 6 0 0 1 0 8.4m-8.4 0a6 6 0 0 1 0-8.4"/></svg>
              <span>Once</span>
            </button>
          </div>
        </div>
      </div>
    `;

    let marker = this.nodeMarkers.get(nodeNum);
    if (marker) {
      marker.setLatLng(latLng);
      marker.setIcon(icon);
      marker.setPopupContent(popupContent);
    } else {
      marker = L.marker(latLng, { icon });
      marker.bindPopup(popupContent, { className: 'tactical-popup', closeButton: false });
      this.nodeLayer.addLayer(marker);
      this.nodeMarkers.set(nodeNum, marker);
    }
  }

  /**
   * Synchronizes the visual opacity of all known node nameplate badges on the map.
   *
   * Why: Dims nodes that do not contribute to coloring any hexagon cell to 50% opacity,
   * keeping the operator focused on high-performing, dominant relay stations.
   */
  private updateNodeMarkerOpacities(): void {
    for (const [nodeNum, marker] of this.nodeMarkers.entries()) {
      const isContributing = this.hexContributingNodes.has(nodeNum >>> 0);
      const markerEl = marker.getElement();
      if (markerEl) {
        const plate = markerEl.querySelector('.node-nameplate');
        if (plate) {
          plate.classList.toggle('dimmed', !isContributing);
        }
      }
    }
  }

  /**
   * Retrieves set of node numbers that currently contribute to hexagon coloring.
   *
   * Why: Enables callers and test suites to verify which nodes dominate coverage cells.
   */
  getHexContributingNodes(): Set<number> {
    return new Set(this.hexContributingNodes);
  }

  /**
   * Renders all known nodes that have valid GPS positions and reconciles stale markers.
   *
   * @param nodes - Array of StoredNodes
   */
  renderAllKnownNodes(nodes: StoredNode[]): void {
    const validNodes = nodes.filter(
      (n) => typeof n.lat === 'number' && typeof n.lon === 'number' && (n.lat !== 0 || n.lon !== 0)
    );
    const activeNodeNumSet = new Set(validNodes.map((n) => Number(n.num) >>> 0));

    // Remove any markers from the map that are no longer part of known nodes
    for (const [num, marker] of this.nodeMarkers.entries()) {
      if (!activeNodeNumSet.has(num)) {
        this.nodeLayer.removeLayer(marker);
        this.nodeMarkers.delete(num);
        this.nodePrevCoord.delete(num);
      }
    }

    this.nodeCoordsMap.clear();
    this.nodePrevCoord.clear();

    for (const node of validNodes) {
      this.updateNodeMarker(node);
    }
  }

  /**
   * Adjusts the map view to fit all known node positions.
   */
  fitNodesBounds(): void {
    if (!this.map || !this.showNodeMarkers || this.nodeMarkers.size === 0) return;

    const coords: [number, number][] = [];
    for (const marker of this.nodeMarkers.values()) {
      const ll = marker.getLatLng();
      coords.push([ll.lat, ll.lng]);
    }

    if (coords.length > 0) {
      this.map.fitBounds(coords, { padding: [50, 50], maxZoom: 15 });
    }
  }

  /**
   * Resolves the transmitting node identity and color for a packet.
   *
   * Why: Meshtastic packets can arrive either directly from the origin sender or retransmitted via
   * intermediate mesh relays. Resolving 8-bit relay bytes to matched NodeDB entries ensures
   * identical chromatic identities between raw tracking points and aggregated hexagonal sectors.
   *
   * @param packet - Packet to resolve
   * @returns Resolved transmitting node metadata
   */
  resolveTransmittingNode(packet: StoredPacket): {
    nodeId: number;
    nodeName: string;
    color: string;
    isRelayed: boolean;
  } {
    let nodeId = packet.from;
    let nodeName = packet.fromName;
    let isRelayed = false;

    if (packet.relayNode && packet.relayNode !== 0) {
      isRelayed = true;
      const matchedRelay = this.fusionEngine.findNodeBy8BitRelay(
        packet.relayNode,
        packet.lat,
        packet.lon
      );
      if (matchedRelay) {
        nodeId = matchedRelay.num;
        nodeName = matchedRelay.shortName || matchedRelay.hexId;
      } else {
        nodeId = packet.relayNode & 0xff;
        nodeName = packet.relayName || `Relay 0x${(packet.relayNode & 0xff).toString(16)}`;
      }
    }

    const color = getNodeColor(nodeId, nodeName);
    return { nodeId, nodeName, color, isRelayed };
  }

  /**
   * Adds an individual packet to the map, applying spatial clustering with vibrant node coloring
   * and non-accumulating SNR opacity.
   *
   * @param packet - Newly stored packet
   */
  addPacket(packet: StoredPacket): void {
    if (!this.map || !packet.hasGeo || packet.lat === null || packet.lon === null) {
      return;
    }

    this.activePackets.push(packet);

    // 1. Resolve transmitting last-hop node (relay or direct sender)
    const tx = this.resolveTransmittingNode(packet);

    // 2. Record packet in SpatialCoverageManager (prevents opacity stacking on stationary reception)
    const { spot, isNew } = this.spatialManager.recordPacket(
      packet,
      tx.nodeId,
      tx.nodeName,
      tx.isRelayed
    );

    if (isNew) {
      const marker = L.circleMarker([spot.lat, spot.lon], {
        radius: 7,
        color: spot.color,
        weight: 1.5,
        fillColor: spot.color,
        fillOpacity: spot.opacity,
        renderer: this.canvasRenderer
      });

      marker.bindPopup(this.buildSpotPopupContent(spot), { className: 'tactical-popup', closeButton: false });

      // On-demand vector drawing: click opens vector to transmitting node
      marker.on('click', () => {
        const latestPkt = spot.packets[spot.packets.length - 1];
        this.showInspectionVector(latestPkt);
      });

      marker.on('popupclose', () => {
        this.clearInspectionVector();
      });

      this.markerLayer.addLayer(marker);
      this.spotMarkers.set(spot.id, marker);
    } else {
      const existingMarker = this.spotMarkers.get(spot.id);
      if (existingMarker) {
        existingMarker.setStyle({
          fillOpacity: spot.opacity
        });
        existingMarker.setPopupContent(this.buildSpotPopupContent(spot));
      }
    }

    // 3. Update Heatmap point
    const heatWeight = normalizeSnrForHeat(packet.snr);
    this.heatPoints.push([packet.lat, packet.lon, heatWeight]);
    if (this.heatLayer) {
      this.heatLayer.setLatLngs(this.heatPoints);
    }

    // 4. Update Hexagrid if active
    if (this.displayMode === 'hexagrid') {
      this.scheduleHexRender();
    }
  }

  /**
   * Generates tactical popup HTML for a spatial coverage spot.
   *
   * @param spot - Coverage spot data
   * @returns Formatted HTML string
   */
  private buildSpotPopupContent(spot: CoverageSpotData): string {
    const latestPkt = spot.packets[spot.packets.length - 1];
    const sender = this.fusionEngine.getKnownNode(latestPkt.from);
    let distanceInfo = '';
    if (sender && typeof sender.lat === 'number' && typeof sender.lon === 'number') {
      const dist = getDistanceKm(spot.lat, spot.lon, sender.lat, sender.lon);
      distanceInfo = `<b>Distanz zur Node:</b> ~${dist} km<br/>`;
    }

    const modeText = spot.isRelayed ? `Relay (${spot.nodeName})` : `Direkt (${spot.nodeName})`;

    return `
      <div style="font-family: monospace; font-size: 12px; color: #fff; background: #0f172a; padding: 5px; border-radius: 4px; border-left: 3px solid ${spot.color}; min-width: 200px;">
        <div style="color: ${spot.color}; font-weight: bold; border-bottom: 1px solid #334155; padding-bottom: 3px;">
          <span style="color: ${spot.color};">●</span> Empfangspunkt: ${spot.nodeName}
        </div>
        <div style="margin-top: 5px; line-height: 1.5;">
          <b>Transmitter:</b> <span style="color: ${spot.color};">${modeText}</span><br/>
          <b>Bester SNR:</b> <span style="color: ${spot.color}; font-weight: bold;">${spot.maxSnr} dB</span> (${Math.round(spot.opacity * 100)}% Deckkraft)<br/>
          <b>Letzter RSSI:</b> ${spot.latestRssi} dBm<br/>
          <b>Pakete an dieser Stelle:</b> ${spot.packetCount} (keine Akkumulation)<br/>
          ${distanceInfo}
          <b>Letzter Absender:</b> ${latestPkt.fromName} (${latestPkt.fromHex})<br/>
          <b>Hops Taken:</b> ${latestPkt.hopsTaken}<br/>
          <b>Port:</b> ${latestPkt.portName}<br/>
          <b>Zuletzt empfangen:</b> ${new Date(spot.lastHeard).toLocaleTimeString()}<br/>
          ${latestPkt.payloadSummary ? `<b>Payload:</b> ${latestPkt.payloadSummary}` : ''}
        </div>
        <div style="margin-top: 5px; font-size: 10px; color: #00f3ff; border-top: 1px solid #334155; padding-top: 3px;">
          📡 Klick aktiviert Verbindungslinie zur Node
        </div>
      </div>
    `;
  }

  /**
   * Draws an on-demand inspection vector connecting the clicked packet reception point
   * with the transmitting node (and relay if multi-hop) using vibrant node colors.
   *
   * @param packet - The selected packet to inspect
   */
  showInspectionVector(packet: StoredPacket): void {
    this.vectorLayer.clearLayers();
    if (!packet.lat || !packet.lon) return;

    const sender = this.fusionEngine.getKnownNode(packet.from);
    let relayNode: StoredNode | null = null;

    if (packet.relayNode && packet.relayNode !== 0) {
      relayNode = this.fusionEngine.findNodeBy8BitRelay(packet.relayNode, packet.lat, packet.lon);
    }

    const lastHopId = relayNode ? relayNode.num : packet.from;
    const lastHopName = relayNode ? relayNode.shortName : packet.fromName;
    const vectorColor = getNodeColor(lastHopId, lastHopName);

    // Case 1: Multi-hop packet with identified relay having coordinates
    if (relayNode && typeof relayNode.lat === 'number' && typeof relayNode.lon === 'number') {
      const distRxToRelay = getDistanceKm(packet.lat, packet.lon, relayNode.lat, relayNode.lon);

      // Line 1: Rx position to Relay
      const relayLine = L.polyline(
        [
          [packet.lat, packet.lon],
          [relayNode.lat, relayNode.lon]
        ],
        {
          color: vectorColor,
          weight: 3,
          opacity: 0.9,
          dashArray: '8, 8'
        }
      );
      relayLine.bindTooltip(`Last Hop: ${relayNode.shortName} (~${distRxToRelay} km)`, {
        permanent: true,
        direction: 'center',
        className: 'tactical-tooltip'
      });
      this.vectorLayer.addLayer(relayLine);

      // Line 2: Relay to Sender (if sender location known)
      if (
        sender &&
        typeof sender.lat === 'number' &&
        typeof sender.lon === 'number' &&
        sender.num !== relayNode.num
      ) {
        const senderColor = getNodeColor(sender.num, sender.shortName);
        const distRelayToSender = getDistanceKm(relayNode.lat, relayNode.lon, sender.lat, sender.lon);
        const originLine = L.polyline(
          [
            [relayNode.lat, relayNode.lon],
            [sender.lat, sender.lon]
          ],
          {
            color: senderColor,
            weight: 2,
            opacity: 0.75,
            dashArray: '4, 6'
          }
        );
        originLine.bindTooltip(`Hop: ${sender.shortName} → ${relayNode.shortName} (~${distRelayToSender} km)`, {
          sticky: true,
          className: 'tactical-tooltip'
        });
        this.vectorLayer.addLayer(originLine);
      }
    } else if (sender && typeof sender.lat === 'number' && typeof sender.lon === 'number') {
      // Case 2: Direct link to sender
      const dist = getDistanceKm(packet.lat, packet.lon, sender.lat, sender.lon);
      const directLine = L.polyline(
        [
          [packet.lat, packet.lon],
          [sender.lat, sender.lon]
        ],
        {
          color: vectorColor,
          weight: 3,
          opacity: 0.9,
          dashArray: '8, 8'
        }
      );
      directLine.bindTooltip(`Direkt: ${sender.shortName} (~${dist} km)`, {
        permanent: true,
        direction: 'center',
        className: 'tactical-tooltip'
      });
      this.vectorLayer.addLayer(directLine);
    }
  }

  /**
   * Clears any active inspection vector line from the map.
   */
  clearInspectionVector(): void {
    this.vectorLayer.clearLayers();
  }

  /**
   * Updates the current real-time GPS position marker and appends to the track polyline.
   *
   * @param snapshot - Current GPS fix
   */
  updateCurrentLocation(snapshot: GeoSnapshot): void {
    if (!this.map) return;

    this.lastKnownLocation = snapshot;
    const latLng: [number, number] = [snapshot.lat, snapshot.lon];

    if (!this.userLocationMarker) {
      // First fix: center map and create glowing user position beacon
      this.map.setView(latLng, 15);
      this.userLocationMarker = L.circleMarker(latLng, {
        radius: 8,
        color: '#ffffff',
        weight: 2,
        fillColor: '#00f3ff',
        fillOpacity: 1,
        renderer: this.canvasRenderer
      }).addTo(this.map);
    } else {
      this.userLocationMarker.setLatLng(latLng);
    }

    // Append to breadcrumb track (thinned to >= 5 meters to prevent memory bloat and vertex explosion)
    const lastCoord = this.trackCoords[this.trackCoords.length - 1];
    if (!lastCoord || calculateGeoDistanceKm(lastCoord[0], lastCoord[1], snapshot.lat, snapshot.lon) >= 0.005) {
      this.trackCoords.push(latLng);
      if (this.trackPolyline) {
        this.trackPolyline.setLatLngs(this.trackCoords);
      }
    }

    // Smoothly pan camera if GPS follow lock is active
    if (this.isFollowingLocation && this.userLocationMarker) {
      this.map.panTo(latLng, { animate: true, duration: 0.5 });
    }
  }

  /**
   * Retrieves recorded breadcrumb track coordinates.
   *
   * @returns Array of [lat, lon] tuples
   */
  getTrackCoords(): [number, number][] {
    return this.trackCoords;
  }

  /**
   * Clears all rendered packet markers, spatial coverage spots, heat points, and routing vectors.
   * Retains the fixed nodes layer.
   */
  clear(): void {
    this.markerLayer.clearLayers();
    this.hexLayer.clearLayers();
    this.vectorLayer.clearLayers();
    this.spotMarkers.clear();
    this.spatialManager.clear();
    this.activePackets = [];
    this.heatPoints = [];
    this.trackCoords = [];
    if (this.heatLayer) {
      this.heatLayer.setLatLngs([]);
    }
    if (this.trackPolyline) {
      this.trackPolyline.setLatLngs([]);
    }
    this.hexContributingNodes.clear();
    this.updateNodeMarkerOpacities();
  }

  /**
   * Replaces map content with a filtered list of packets.
   *
   * @param packets - Array of packets to render
   */
  renderPackets(packets: StoredPacket[]): void {
    this.clear();
    for (const p of packets) {
      this.addPacket(p);
    }
    if (this.displayMode === 'hexagrid') {
      this.renderHexGrid();
    }
  }

  /**
   * Retrieves the currently active basemap identifier.
   *
   * Why: Enables tactical layer controls to query the current basemap state on open.
   */
  getActiveBasemap(): 'osm-dark' | 'osm-standard' | 'satellite' {
    return this.currentBasemap;
  }

  /**
   * Switches the active base tile layer programmatically.
   *
   * @param type - Basemap type ('osm-dark', 'osm-standard', 'satellite')
   */
  switchBasemap(type: 'osm-dark' | 'osm-standard' | 'satellite'): void {
    this.currentBasemap = type;
    if (!this.map) return;
    for (const layer of Object.values(this.baseLayers)) {
      if (this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    }
    const target = this.baseLayers[type];
    if (target) {
      target.addTo(this.map);
    }
  }

  getMap(): L.Map | null {
    return this.map;
  }

  /**
   * Closes any currently opened popup on the Leaflet map.
   *
   * Why: Enables tactical controls and interaction dialogs (such as isolating a node)
   * to dismiss the active info card without forcing the user to manually click away.
   */
  closePopup(): void {
    if (this.map) {
      this.map.closePopup();
    }
  }

  /**
   * Enables or disables real-time camera tracking following the user GPS position.
   *
   * Why: Keeps the map viewport centered on the moving vehicle or surveyor in tracking mode.
   * Toggling this automatically synchronizes the map center and notifies registered UI listeners.
   *
   * @param follow - True to lock camera to GPS, false to unlock
   * @param immediateSnapshot - Optional snapshot used to immediately center the map
   */
  setFollowLocation(follow: boolean, immediateSnapshot?: GeoSnapshot): void {
    if (immediateSnapshot) {
      this.lastKnownLocation = immediateSnapshot;
    }

    if (this.isFollowingLocation === follow) {
      // If already active, re-center on current location at appropriate zoom
      if (follow && this.lastKnownLocation && this.map) {
        this.map.setView(
          [this.lastKnownLocation.lat, this.lastKnownLocation.lon],
          Math.max(this.map.getZoom(), 16)
        );
      }
      return;
    }

    this.isFollowingLocation = follow;

    if (this.isFollowingLocation && this.lastKnownLocation && this.map) {
      this.map.setView(
        [this.lastKnownLocation.lat, this.lastKnownLocation.lon],
        Math.max(this.map.getZoom(), 16)
      );
    }

    this.followLocationListeners.forEach((cb) => {
      try {
        cb(this.isFollowingLocation);
      } catch (err) {
        console.error('[MapManager] Error in followLocation listener:', err);
      }
    });
  }

  /**
   * Retrieves whether the map camera is actively tracking the GPS location.
   *
   * Why: Enables UI controls (such as the recenter HUD button) to reflect current tracking state.
   */
  isFollowingLocationActive(): boolean {
    return this.isFollowingLocation;
  }

  /**
   * Subscribes to changes in GPS camera follow state.
   *
   * Why: Decouples UI indicators from internal drag-release and programmatic toggle events.
   *
   * @param callback - Function invoked with true when tracking engages, false when released
   */
  onFollowLocationChange(callback: (active: boolean) => void): void {
    this.followLocationListeners.push(callback);
  }
}
